import { useEffect } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { format } from 'date-fns';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { Drawer } from '../../components/Drawer/Drawer';
import { useToast } from '../../components/Toast';
import { cn } from '../../lib/cn';
import { errorMessage } from '../../lib/errorMessage';
import {
  formatILS,
  PAYMENT_METHOD_LABELS_HE,
  PAYMENT_METHODS,
  type InvoiceBalance,
  type ProjectInvoice,
  type ProjectPayment,
} from './projectFinance';
import { useUpsertPayment } from './projectFinanceQueries';
import styles from './projectFinance.module.css';

const FORM_ID = 'payment-form';

const schema = z.object({
  amount: z.number({ invalid_type_error: 'הזן סכום' }).positive('הסכום חייב להיות גדול מאפס'),
  paid_at: z.string().min(1, 'שדה חובה'),
  method: z.enum(PAYMENT_METHODS),
  invoice_id: z.string(),
  notes: z.string().max(1000, 'עד 1000 תווים'),
});

type FormValues = z.infer<typeof schema>;

export interface PaymentPreset {
  invoice_id: string;
  amount: number;
}

interface Props {
  open: boolean;
  payment: ProjectPayment | null;
  /** "סמן כשולמה" opens a new payment already attached to the invoice, for its balance. */
  preset: PaymentPreset | null;
  projectId: string;
  invoices: ProjectInvoice[];
  balances: Record<string, InvoiceBalance>;
  orgId: string | null;
  userId: string | null;
  onClose: () => void;
}

export function PaymentDrawer({
  open,
  payment,
  preset,
  projectId,
  invoices,
  balances,
  orgId,
  userId,
  onClose,
}: Props) {
  const toast = useToast();
  const upsert = useUpsertPayment(orgId, userId, projectId);
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  useEffect(() => {
    if (!open) return;
    reset(
      payment
        ? {
            amount: payment.amount,
            paid_at: payment.paid_at,
            method: payment.method,
            invoice_id: payment.invoice_id ?? '',
            notes: payment.notes ?? '',
          }
        : {
            amount: preset?.amount ?? Number.NaN,
            paid_at: format(new Date(), 'yyyy-MM-dd'),
            method: 'transfer',
            invoice_id: preset?.invoice_id ?? '',
            notes: '',
          },
    );
  }, [open, payment, preset, reset]);

  const selectedInvoice = watch('invoice_id');
  const selectedDue = selectedInvoice ? (balances[selectedInvoice]?.due ?? 0) : 0;

  async function onSubmit(values: FormValues) {
    try {
      await upsert.mutateAsync({ id: payment?.id ?? null, values });
      toast.success(payment ? 'התשלום עודכן' : 'התשלום נרשם');
      onClose();
    } catch (err) {
      toast.error(`לא הצלחנו לשמור · ${errorMessage(err)}`);
    }
  }

  const openInvoices = invoices.filter(
    (i) => i.status !== 'cancelled' || i.id === payment?.invoice_id,
  );

  return (
    <Drawer
      open={open}
      onClose={onClose}
      eyebrow="תשלום"
      title={payment ? 'עריכת תשלום' : 'רשום תשלום'}
      ariaLabel={payment ? 'עריכת תשלום' : 'רשום תשלום'}
      footer={
        <>
          <button type="button" className={styles.btnSecondary} onClick={onClose}>
            בטל
          </button>
          <button
            type="submit"
            form={FORM_ID}
            className={styles.btnPrimary}
            disabled={upsert.isPending}
          >
            שמור
          </button>
        </>
      }
    >
      <form
        id={FORM_ID}
        className={styles.form}
        noValidate
        onSubmit={(e) => {
          void handleSubmit(onSubmit)(e);
        }}
      >
        <p className={styles.hint}>
          כסף שהתקבל מהלקוח על הפרויקט. מקדמה לפני חשבונית נרשמת בלי חשבונית.
        </p>

        <div className={styles.row}>
          <div className={styles.field}>
            <label htmlFor="pay-amount">סכום (₪)</label>
            <input
              id="pay-amount"
              type="number"
              min={0}
              step="0.01"
              dir="ltr"
              className={cn(styles.input, errors.amount && styles.invalid)}
              {...register('amount', {
                setValueAs: (v: unknown) => (v === '' || v === null ? Number.NaN : Number(v)),
              })}
            />
            {errors.amount ? <p className={styles.error}>{errors.amount.message}</p> : null}
          </div>
          <div className={styles.field}>
            <label htmlFor="pay-date">תאריך</label>
            <input
              id="pay-date"
              type="date"
              dir="ltr"
              className={cn(styles.input, errors.paid_at && styles.invalid)}
              {...register('paid_at')}
            />
          </div>
        </div>

        <div className={styles.row}>
          <div className={styles.field}>
            <label htmlFor="pay-method">אמצעי תשלום</label>
            <select id="pay-method" className={styles.input} {...register('method')}>
              {PAYMENT_METHODS.map((m) => (
                <option key={m} value={m}>
                  {PAYMENT_METHOD_LABELS_HE[m]}
                </option>
              ))}
            </select>
          </div>
          <div className={styles.field}>
            <label htmlFor="pay-invoice">על חשבונית</label>
            <select id="pay-invoice" className={styles.input} {...register('invoice_id')}>
              <option value="">בלי חשבונית (מקדמה)</option>
              {openInvoices.map((i) => (
                <option key={i.id} value={i.id}>
                  {i.invoice_number} · {formatILS(i.amount)}
                </option>
              ))}
            </select>
          </div>
        </div>
        {selectedInvoice && selectedDue > 0 && !payment ? (
          <button
            type="button"
            className={styles.fillBtn}
            onClick={() => {
              setValue('amount', selectedDue, { shouldValidate: true });
            }}
          >
            מלא את היתרה בחשבונית: <bdi>{formatILS(selectedDue)}</bdi>
          </button>
        ) : null}

        <div className={styles.field}>
          <label htmlFor="pay-notes">הערות</label>
          <textarea
            id="pay-notes"
            rows={3}
            className={cn(styles.input, styles.textarea)}
            {...register('notes')}
          />
        </div>
      </form>
    </Drawer>
  );
}
